import { useEffect, useState } from 'react';
import { ControlLabel, FormControl, FormGroup, HelpBlock, Modal } from 'react-bootstrap';
import { IOpoCase } from '@nkr/models/opo-case';
import { OpoModalButtons, OpoModalForm } from '../landing-page/opo-styles';
import SegmentedChoice from '../landing-page/settings/packaging/segmented-choice';
import { ICaseClosing } from './case-edit-modal';

interface IProps {
    opoCase: IOpoCase | null,
    onConfirm: (closing: ICaseClosing) => void,
    onCancel: () => void
}

const CaseCloseModal = ({ opoCase, onConfirm, onCancel }: IProps) => {
    const [wentToIntendedCenter, setWentToIntendedCenter] = useState<boolean | null>(null);
    const [actualPickupDateTime, setActualPickupDateTime] = useState('');
    const [actualDeliveryDateTime, setActualDeliveryDateTime] = useState('');
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        setWentToIntendedCenter(null);
        setActualPickupDateTime('');
        setActualDeliveryDateTime('');
        setSubmitted(false);
    }, [opoCase]);

    const errors = {
        actualPickupDateTime: actualPickupDateTime == '' ? 'Actual pickup is required' : '',
        actualDeliveryDateTime: actualDeliveryDateTime == ''
            ? 'Actual delivery is required'
            : actualPickupDateTime != '' && actualDeliveryDateTime < actualPickupDateTime ? 'Actual delivery cannot be before actual pickup' : ''
    };

    const handleConfirm = () => {
        if (wentToIntendedCenter && (errors.actualPickupDateTime || errors.actualDeliveryDateTime)) {
            setSubmitted(true);
            return;
        }

        onConfirm({
            wentToIntendedCenter,
            actualPickupDateTime: wentToIntendedCenter ? actualPickupDateTime : '',
            actualDeliveryDateTime: wentToIntendedCenter ? actualDeliveryDateTime : ''
        });
    };

    const renderDateTime = (label: string, value: string, error: string, onChange: (value: string) => void) => (
        <FormGroup className={submitted && error ? 'has-error' : null}>
            <ControlLabel><span className='required'>*</span> {label}</ControlLabel>
            <FormControl type='datetime-local' value={value} onChange={(e: any) => onChange(e.target.value)} />
            {submitted && error && (
                <HelpBlock>{error}</HelpBlock>
            )}
        </FormGroup>
    );

    return (
        <Modal show={opoCase != null} onHide={onCancel} backdrop='static'>
            <Modal.Header closeButton>
                <Modal.Title>Close Case {opoCase?.caseNumber}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <OpoModalForm>
                    <div className='form-grid'>
                        <FormGroup className='full-width'>
                            <ControlLabel><span className='required'>*</span> Did the organ go to the intended recipient center{opoCase?.intendedCenterAlias ? ` (${opoCase.intendedCenterAlias})` : ''}?</ControlLabel>
                            <SegmentedChoice
                                label='Did the organ go to the intended recipient center?'
                                value={wentToIntendedCenter}
                                options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]}
                                disabled={false}
                                onChange={setWentToIntendedCenter}
                            />
                        </FormGroup>

                        {wentToIntendedCenter && renderDateTime('Actual Pickup', actualPickupDateTime, errors.actualPickupDateTime, setActualPickupDateTime)}
                        {wentToIntendedCenter && renderDateTime('Actual Delivery', actualDeliveryDateTime, errors.actualDeliveryDateTime, setActualDeliveryDateTime)}
                    </div>
                </OpoModalForm>
            </Modal.Body>
            <Modal.Footer>
                <OpoModalButtons>
                    <button type='button' className='btn btn-primary' disabled={wentToIntendedCenter == null} onClick={handleConfirm}>
                        {wentToIntendedCenter == false ? 'Continue' : 'Close Case'}
                    </button>
                    <button type='button' className='btn' onClick={onCancel}>Cancel</button>
                </OpoModalButtons>
            </Modal.Footer>
        </Modal>
    );
}

export default CaseCloseModal;
